import { useEffect, useState } from 'react';
import { ArrowUp, ExternalLink, MessageSquare, UserRound, X } from 'lucide-react';
import { Button, cn } from '@respectly/ui';
import type { ChatBrandDto, ChatKnowledgeDoc } from '@respectly/shared-types';
import { CHAT_THEME_DEFAULTS, expandChatTheme } from '@respectly/shared-types';
import type { ChatBrandDraft } from '../../stores/useChatWidgetsStore';

/** The widget's own stack (`--rc-font`): never a webfont on someone else's page. */
const WIDGET_FONT =
  'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

/**
 * A static rendering of the widget against the current (possibly unsaved)
 * configuration.
 *
 * Deliberately a **mock**, not the real custom element: the widget is Preact with
 * its own JSX runtime, and compiling it into this React app would be a build
 * coupling that could let the two drift without anyone noticing. `LiveWidgetToggle`
 * below runs the real thing when byte-for-byte fidelity is what you need.
 *
 * The irony is that this mock drifted anyway. It reproduced the panel as it was
 * *before* the rework — a serif greeting, answers as full-width prose against a
 * left rule, a strip of uppercase monospace source labels — none of which the
 * widget has rendered for some time. Worse, it painted the wrong tokens onto the
 * wrong elements: the launcher took Ink where the real one takes Primary, and the
 * visitor's own bubble took Quiet where the real one is the single place Primary
 * fills. An admin changing "Ink" to recolour the button watched this preview
 * change and the deployed widget not.
 *
 * So every element below is anchored to a rule in
 * `apps/chat-widget/src/panel/styles.ts`, named in a comment beside it. The
 * panel's own signature — the presence rail, which always says who you are
 * talking to and what state the conversation is in — leads here as it does there.
 */
export function WidgetPreview({
  brand,
  draft,
  docs,
}: {
  brand: ChatBrandDto;
  draft: ChatBrandDraft;
  docs: ChatKnowledgeDoc[];
}) {
  const saved = { ...brand.theme, ...(draft.theme ?? {}) };
  // Through the same derivation the server applies, so what is drawn here is what
  // the widget will be handed — hairlines, washes, signal colours and all. The
  // preview used to read the stored record directly, which was fine while the
  // stored record *was* the palette; it no longer is.
  const theme = expandChatTheme({ ...CHAT_THEME_DEFAULTS, ...saved }) as Record<string, string>;
  const greeting = (draft.greeting ?? brand.greeting) || 'Ask me a question.';
  const samplePrompts = draft.samplePrompts ?? brand.samplePrompts;
  const knowledgeSlugs = draft.knowledgeSlugs ?? brand.knowledgeSlugs;
  const features = draft.features ?? brand.features;
  const displayName = draft.displayName ?? brand.displayName;
  const position = draft.position ?? brand.position;
  const handoffEnabled = features.handoff !== false;
  /** Matches the widget: any saved colour turns off the automatic dark panel. */
  const followsSystemDark = Object.keys(saved).length === 0;

  // Name the first two selected documents on the sources line, so it reflects
  // this widget's actual knowledge rather than a generic placeholder.
  const cited = knowledgeSlugs
    .slice(0, 2)
    .map((slug) => docs.find((d) => d.slug === slug)?.title ?? slug);

  const initials = displayName.slice(0, 2).toUpperCase();

  return (
    <div className="space-y-3">
      <div
        className="overflow-hidden rounded-[1.75rem] border shadow-lg"
        style={{
          background: theme.groundColor,
          borderColor: theme.hairlineColor,
          // The real widget docks to the viewport edge and rounds only its two
          // outer corners; a preview floating in a page rounds all four.
          fontFamily: WIDGET_FONT,
        }}
      >
        {/* ── The presence rail (`.rail`) — the panel's signature, and now a
             filled band in the header colour rather than more of the panel ── */}
        <div
          className="flex items-center gap-2.5 px-4 py-3"
          style={{ background: theme.headerColor }}
        >
          {/* `.face` — the header pair, inverted */}
          <span
            aria-hidden
            className="grid h-9 w-9 flex-none place-items-center rounded-full text-[13px] font-semibold"
            style={{ background: theme.onHeaderColor, color: theme.headerColor }}
          >
            {initials}
          </span>
          <div className="min-w-0 flex-1">
            {/* `.who h2` */}
            <p
              className="truncate text-[15px] font-semibold leading-tight"
              style={{ color: theme.onHeaderColor }}
            >
              {displayName}
            </p>
            {/* `.status`, with the `.pip` — both on the band, so both take the
                header's own text colour rather than the panel's */}
            <p
              className="mt-px flex items-center gap-1.5 text-xs"
              style={{ color: theme.onHeaderSoftColor }}
            >
              <span
                className="inline-block h-1.5 w-1.5 flex-none rounded-full"
                style={{ background: theme.onHeaderColor }}
              />
              Answers from your documentation
            </p>
          </div>
        </div>

        <div className="space-y-4 px-4 pb-4 pt-4">
          {/* `.greeting` — one typeface; the serif is gone */}
          <p
            className="text-[18px] font-semibold leading-snug tracking-[-0.016em]"
            style={{ color: theme.inkColor }}
          >
            {greeting}
          </p>

          {/* `.prompt` — Surface fill, Hairline border */}
          {samplePrompts.length > 0 && (
            <div className="space-y-1.5">
              {samplePrompts.slice(0, 3).map((prompt) => (
                <div
                  key={prompt}
                  className="rounded-xl px-3 py-2.5 text-sm"
                  style={{
                    border: `1px solid ${theme.hairlineColor}`,
                    background: theme.surfaceColor,
                    color: theme.inkColor,
                  }}
                >
                  {prompt}
                </div>
              ))}
            </div>
          )}

          {/* `.ask` — the one place the brand colour fills */}
          <div className="flex justify-end">
            <div
              className="max-w-[84%] rounded-2xl rounded-br-sm px-3 py-2 text-[13px]"
              style={{ background: theme.primaryColor, color: theme.onPrimaryColor }}
            >
              {samplePrompts[0] ?? 'Do I need consent to text my customers?'}
            </div>
          </div>

          {/* `.said` — a bubble on Surface, not prose against a rule */}
          <div
            className="max-w-[92%] rounded-2xl rounded-bl-sm px-3.5 py-2.5"
            style={{ background: theme.surfaceColor, border: `1px solid ${theme.hairlineColor}` }}
          >
            <p className="text-[13px] leading-relaxed" style={{ color: theme.inkColor }}>
              {knowledgeSlugs.length === 0 ? (
                "I can't confirm that from our documentation. I can put you in touch with someone who can answer it properly."
              ) : (
                <>
                  Yes — marketing texts need express written consent, and Respectly records each
                  one as{' '}
                  {/* `.prose code` is the one place Quiet is visible at rest, so the
                      preview earns that swatch by showing it. */}
                  <code
                    className="whitespace-nowrap rounded px-1 py-0.5 font-mono text-[0.86em]"
                    style={{ background: theme.quietColor }}
                  >
                    PRE_MARKETING
                  </code>{' '}
                  with the date it was given.
                </>
              )}
            </p>

            {/* `.sources` — one sentence-case line in Soft ink */}
            {cited.length > 0 && (
              <p
                className="mt-2 truncate pt-2 text-[13px]"
                style={{
                  borderTop: `1px solid ${theme.hairlineColor}`,
                  color: theme.softInkColor,
                }}
              >
                Based on {cited.join(' and ')}
              </p>
            )}
          </div>

          {/* `.person` — a text tint, never a fill. The one warm colour on the
              panel, spent only on a human being in the conversation. */}
          {handoffEnabled && (
            <p
              className="flex items-center gap-1.5 text-[13px] font-semibold"
              style={{ color: theme.handoffColor }}
            >
              <UserRound className="h-[15px] w-[15px]" />
              Talk to a person
            </p>
          )}
        </div>

        {/* `.composer` / `.field` / `.send` */}
        <div
          className="px-4 py-2.5"
          style={{ borderTop: `1px solid ${theme.hairlineColor}`, background: theme.groundColor }}
        >
          <div
            className="flex items-center gap-1.5 rounded-[1.375rem] py-1 pl-3.5 pr-1"
            style={{ border: `1px solid ${theme.hairlineColor}`, background: theme.surfaceColor }}
          >
            <span className="flex-1 text-[15px]" style={{ color: theme.softInkColor }}>
              Ask a question…
            </span>
            <span
              className="grid h-9 w-9 flex-none place-items-center rounded-full"
              style={{ background: theme.primaryColor, color: theme.onPrimaryColor }}
            >
              <ArrowUp className="h-[17px] w-[17px]" />
            </span>
          </div>
        </div>
      </div>

      {/* The dock: Primary fill, On-primary label — and it sits in the
          configured corner, which this preview used to ignore entirely.

          The trailing × is the visitor's control, not an admin setting. It
          collapses the pill to a circle in the same corner and is remembered for
          that browser, so someone reading the page underneath can get the widget
          out of the way without losing the way back to it. Shown here because an
          admin approving a palette should see every part of what ships. */}
      <div className={cn('flex', position === 'bottom-left' ? 'justify-start' : 'justify-end')}>
        <div
          className="flex items-stretch rounded-full text-[13px] font-semibold shadow-lg"
          style={{ background: theme.primaryColor, color: theme.onPrimaryColor }}
        >
          <span className="flex items-center gap-2 py-2.5 pl-4 pr-2">
            <MessageSquare className="h-4 w-4" />
            Ask about {displayName}
          </span>
          <span
            aria-hidden
            className="grid w-10 flex-none place-items-center rounded-r-full opacity-75"
            style={{ boxShadow: 'inset 1px 0 0 rgba(0, 0, 0, 0.14)' }}
          >
            <X className="h-4 w-4" />
          </span>
        </div>
      </div>

      <p className="text-[11px] leading-relaxed text-slate-400">
        A rendering of the configuration <strong>including unsaved edits</strong>. It does not call
        the model.{' '}
        {followsSystemDark
          ? 'No colours are set, so the widget switches to a dark panel for visitors whose device is in dark mode.'
          : 'Colours are set, so every visitor sees this palette.'}
      </p>

      <LiveWidgetToggle brandId={brand.brandId} />
    </div>
  );
}

/**
 * Mount the real widget, against the saved configuration.
 *
 * Loads the same artifact a customer's page loads, from the CDN, rather than
 * importing the widget's source into this bundle: the widget is Preact with its
 * own JSX runtime, and compiling it into a React app would be a build coupling
 * that could let the preview and the shipped widget drift apart without anyone
 * noticing. What renders here is byte-for-byte what a visitor gets.
 *
 * It reads the *saved* config, so this is the check to run after saving and
 * before the change reaches a public site. The panel above is the one that
 * reflects what you are still typing.
 */
function LiveWidgetToggle({ brandId }: { brandId: string }) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const widgetUrl =
    import.meta.env.VITE_CHAT_WIDGET_URL || 'https://cdn.respectly.ai/respectly-chat.js';
  const apiBase = import.meta.env.VITE_API_URL || window.location.origin;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const mount = () => {
      if (cancelled) return;
      const element = document.createElement('respectly-chat');
      element.setAttribute('brand', brandId);
      element.setAttribute('api-base', apiBase);
      element.id = 'rc-live-preview';
      document.body.appendChild(element);
    };

    if (customElements.get('respectly-chat')) {
      mount();
    } else {
      const script = document.createElement('script');
      script.type = 'module';
      script.src = widgetUrl;
      script.onload = mount;
      script.onerror = () => {
        if (!cancelled) {
          setError(`Could not load the widget from ${widgetUrl}.`);
          setOpen(false);
        }
      };
      document.head.appendChild(script);
    }

    return () => {
      cancelled = true;
      document.getElementById('rc-live-preview')?.remove();
    };
  }, [open, brandId, apiBase, widgetUrl]);

  return (
    <div className="space-y-1.5">
      <Button variant="outline" size="sm" onClick={() => setOpen(!open)} className="w-full">
        <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
        {open ? 'Close the live widget' : 'Open the live widget'}
      </Button>
      <p className="text-[11px] leading-relaxed text-slate-400">
        {open
          ? 'The real widget is running in the corner of this page, against the saved configuration. It will answer for real.'
          : 'Runs the shipped widget against the saved configuration — the same bundle a visitor loads.'}
      </p>
      {error && <p className="text-[11px] text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
}
